import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Upload, 
  DollarSign, 
  CreditCard, 
  User, 
  Mail, 
  Phone,
  Send,
  AlertCircle,
  ChevronLeft,
  Hash,
  Trash2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const PaymentProofUpload = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [project, setProject] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    donorName: '',
    donorEmail: '',
    donorPhone: '',
    amount: '',
    paymentMethod: 'UPI',
    transactionId: '',
    notes: ''
  });
  const [screenshotPreview, setScreenshotPreview] = useState(null);
  const [screenshotFile, setScreenshotFile] = useState(null);

  useEffect(() => {
    const fetchProjectAndBankDetails = async () => {
      try {
        setLoading(true);
        
        if (!projectId) {
          toast.error('Project ID is missing');
          navigate('/projects');
          return;
        }
        
        // Try multiple possible endpoints to handle different server configurations
        const possibleProjectEndpoints = [
          `http://localhost:3011/api/projects/${projectId}`,
          `http://localhost:5000/api/projects/${projectId}`,
          `https://bloodbackend-99n2.onrender.com/api/projects/${projectId}`
        ];
        
        let projectResponse = null;
        
        // Try each endpoint until one works for project details
        for (const endpoint of possibleProjectEndpoints) {
          try {
            console.log(`Trying to fetch project from endpoint: ${endpoint}`);
            
            projectResponse = await axios.get(endpoint, {
              timeout: 5000 // 5 second timeout
            });
            
            // If we get here, the request was successful
            console.log('Project fetched successfully:', projectResponse.data);
            break;
          } catch (err) {
            console.log(`Endpoint ${endpoint} failed:`, err.message);
            // Continue to next endpoint
          }
        }
        
        if (projectResponse && projectResponse.data && projectResponse.data.data) {
          setProject(projectResponse.data.data);
        } else {
          toast.error('Failed to load project details');
          return;
        }
        
        // Fetch bank details for this project
        const possibleBankEndpoints = [
          `http://localhost:3011/api/bank-details/project/${projectId}`,
          `http://localhost:5000/api/bank-details/project/${projectId}`,
          `https://bloodbackend-99n2.onrender.com/api/bank-details/project/${projectId}`
        ];
        
        let bankResponse = null;
        
        // Try each endpoint until one works for bank details
        for (const endpoint of possibleBankEndpoints) {
          try {
            console.log(`Trying to fetch bank details from endpoint: ${endpoint}`);
            
            bankResponse = await axios.get(endpoint, {
              timeout: 5000 // 5 second timeout
            });
            
            // If we get here, the request was successful
            console.log('Bank details fetched successfully:', bankResponse.data);
            break;
          } catch (err) {
            console.log(`Endpoint ${endpoint} failed:`, err.message);
            // Continue to next endpoint
          }
        }
        
        if (bankResponse && bankResponse.data && bankResponse.data.success) {
          setBankDetails(bankResponse.data.data);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchProjectAndBankDetails();
  }, [projectId, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaymentDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleScreenshotChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setScreenshotFile(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setScreenshotPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!screenshotFile) {
      toast.error('Please upload a payment screenshot');
      return;
    }
    
    try {
      setLoading(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('donorName', paymentDetails.donorName);
      formData.append('donorEmail', paymentDetails.donorEmail);
      formData.append('donorPhone', paymentDetails.donorPhone);
      formData.append('amount', paymentDetails.amount);
      formData.append('paymentMethod', paymentDetails.paymentMethod);
      formData.append('transactionId', paymentDetails.transactionId);
      formData.append('notes', paymentDetails.notes);
      formData.append('screenshot', screenshotFile);
      
      // Try multiple possible endpoints to handle different server configurations
      const possibleEndpoints = [
        `http://localhost:3011/api/payment-proofs/project/${projectId}`,
        `http://localhost:5000/api/payment-proofs/project/${projectId}`,
        `https://bloodbackend-99n2.onrender.com/api/payment-proofs/project/${projectId}`
      ];
      
      let response = null;
      let error = null;
      
      // Try each endpoint until one works
      for (const endpoint of possibleEndpoints) {
        try {
          console.log(`Trying to submit payment proof to endpoint: ${endpoint}`);
          
          response = await axios.post(endpoint, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            timeout: 10000 // 10 second timeout
          });
          
          // If we get here, the request was successful
          console.log('Payment proof submitted successfully:', response.data);
          break;
        } catch (err) {
          console.log(`Endpoint ${endpoint} failed:`, err.message);
          error = err;
          // Continue to next endpoint
        }
      }
      
      if (response && response.data && response.data.success) {
        toast.success('Payment proof submitted successfully');
        
        // Reset form
        setPaymentDetails({
          donorName: '',
          donorEmail: '',
          donorPhone: '',
          amount: '',
          paymentMethod: 'UPI',
          transactionId: '',
          notes: ''
        });
        
        setScreenshotFile(null);
        setScreenshotPreview(null);
        
        // Redirect to project page
        navigate(`/projects/${projectId}`);
      } else {
        toast.error('Failed to submit payment proof');
      }
    } catch (error) {
      console.error('Error submitting payment proof:', error);
      toast.error('Failed to submit payment proof');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !project) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                Project not found. Please check the URL and try again.
              </p>
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate('/projects')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
        >
          <ChevronLeft size={16} className="mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <div className="mb-6">
        <button
          onClick={() => navigate(`/projects/${projectId}`)}
          className="inline-flex items-center text-blue-600 hover:text-blue-800"
        >
          <ChevronLeft size={16} className="mr-1" />
          Back to Project
        </button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
          Submit Payment Proof
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mt-1">
          For project: {project.title}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Payment Information */}
        <div className="md:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
              Payment Details
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Donor Name */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                    Your Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="donorName"
                      value={paymentDetails.donorName}
                      onChange={handleChange}
                      required
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter your name"
                    />
                  </div>
                </div>

                {/* Donor Email */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                    Your Email <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="donorEmail"
                      value={paymentDetails.donorEmail}
                      onChange={handleChange}
                      required
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>

                {/* Donor Phone */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                    Your Phone
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      name="donorPhone"
                      value={paymentDetails.donorPhone}
                      onChange={handleChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter your phone (optional)"
                    />
                  </div>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                    Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="number"
                      name="amount"
                      value={paymentDetails.amount}
                      onChange={handleChange}
                      required
                      min="1"
                      step="1"
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter amount"
                    />
                  </div>
                </div>

                {/* Payment Method */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                    Payment Method <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <CreditCard size={18} className="text-gray-400" />
                    </div>
                    <select
                      name="paymentMethod"
                      value={paymentDetails.paymentMethod}
                      onChange={handleChange}
                      required
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    >
                      <option value="UPI">UPI</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Cash">Cash</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>

                {/* Transaction ID */}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                    Transaction ID
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Hash size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="transactionId"
                      value={paymentDetails.transactionId}
                      onChange={handleChange}
                      className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                      placeholder="Enter transaction ID (optional)"
                    />
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="mt-6">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Additional Notes
                </label>
                <textarea
                  name="notes"
                  value={paymentDetails.notes}
                  onChange={handleChange}
                  rows="3"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Any additional information (optional)"
                ></textarea>
              </div>

              {/* Screenshot Upload */}
              <div className="mt-6">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-medium mb-2">
                  Payment Screenshot <span className="text-red-500">*</span>
                </label>
                <div className="mt-2">
                  {screenshotPreview ? (
                    <div className="relative mb-4">
                      <img 
                        src={screenshotPreview} 
                        alt="Payment Screenshot" 
                        className="w-full h-auto max-h-64 object-contain border rounded-md"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setScreenshotPreview(null);
                          setScreenshotFile(null);
                          document.getElementById('screenshotUpload').value = '';
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full">
                      <label
                        htmlFor="screenshotUpload"
                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600"
                      >
                        <div className="flex flex-col items-center justify-center pt-5 pb-6">
                          <Upload size={24} className="text-gray-500 dark:text-gray-400 mb-2" />
                          <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            PNG, JPG or JPEG (MAX. 5MB)
                          </p>
                        </div>
                        <input
                          id="screenshotUpload"
                          type="file"
                          accept="image/*"
                          onChange={handleScreenshotChange}
                          className="hidden"
                          required
                        />
                      </label>
                    </div>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    Upload a screenshot of your payment confirmation.
                  </p>
                </div>
              </div>

              {/* Submit Button */}
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-700 dark:hover:bg-blue-800"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-t-2 border-b-2 border-white rounded-full animate-spin mr-2"></div>
                  ) : (
                    <Send size={16} className="mr-2" />
                  )}
                  Submit Payment Proof
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Bank Details */}
        <div className="md:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">
              Payment Information
            </h2>

            {bankDetails ? (
              <div>
                {/* QR Code */}
                {bankDetails.qrCodeImage && (
                  <div className="mb-6">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Scan QR Code to Pay
                    </h3>
                    <div className="bg-white p-2 rounded-md border">
                      <img 
                        src={bankDetails.qrCodeImage} 
                        alt="Payment QR Code" 
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}

                {/* Bank Details */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Bank Transfer Details
                  </h3>
                  
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Account Holder</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      {bankDetails.accountHolderName}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Bank Name</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      {bankDetails.bankName}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Account Number</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      {bankDetails.accountNumber}
                    </p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">IFSC Code</p>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      {bankDetails.ifscCode}
                    </p>
                  </div>
                  
                  {bankDetails.upiId && (
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">UPI ID</p>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        {bankDetails.upiId}
                      </p>
                    </div>
                  )}
                </div>

                {/* Instructions */}
                <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900 rounded-md">
                  <p className="text-xs text-blue-700 dark:text-blue-200">
                    After making your payment, please upload a screenshot of the payment 
                    confirmation as proof. Your contribution will help support this project.
                  </p>
                </div>
              </div>
            ) : (
              <div className="p-4 bg-yellow-50 dark:bg-yellow-900 rounded-md">
                <p className="text-sm text-yellow-700 dark:text-yellow-200">
                  Bank details for this project are not available yet. Please contact the 
                  organization directly for payment information.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProofUpload;
